import type { PermitCertificateRequirementRequiredItemDto } from '@/libs/api-rest/generated/model/permitCertificateRequirementRequiredItemDto';
import type { RequirementScopeItemDto } from '@/libs/api-rest/generated/model/requirementScopeItemDto';
import type { AssignedPermitDto } from '@/libs/api-rest/generated/model/assignedPermitDto';
import type { JobRequirement, RequirementType, RequirementLevel, RequirementScopeItem } from '@/raptorjob/domain/types/requirementTypes';

function optionalIntFromApi(value: string | number | null | undefined): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function mapScopeItem(dto: RequirementScopeItemDto): RequirementScopeItem {
  return {
    scopeId: Number(dto.scopeId ?? 0),
    scopeName: dto.scopeName ?? '',
    roleTypeId: optionalIntFromApi(dto.roleTypeId),
    typeOfEquipmentId: optionalIntFromApi(dto.typeOfEquipmentId),
    sizeId: optionalIntFromApi(dto.sizeId),
    totalAssigned: Number(dto.totalAssigned ?? 0),
    totalUnassigned: Number(dto.totalUnassigned ?? 0)
  };
}

export function mapRequirementDto(dto: PermitCertificateRequirementRequiredItemDto): JobRequirement {
  const requirementType: RequirementType = dto.requirementTypeId === 1 ? 'permit' : 'certification';

  let level: RequirementLevel = 'job';
  if (dto.level === 1) {
    level = 'customer';
  } else if (dto.level === 2) {
    level = 'jobSite';
  } else if (dto.level === 3) {
    level = 'job';
  }

  const raw = dto as Record<string, unknown>;

  let scopeType: 'equipment' | 'crewRoles' | undefined;
  if (dto.scope?.scopeType === 1) {
    scopeType = 'equipment';
  } else if (dto.scope?.scopeType === 2) {
    scopeType = 'crewRoles';
  }

  const scopeItems = dto.scope?.scopeItems?.map(mapScopeItem);

  let metCount: number | undefined;
  let requiredCount: number | undefined;
  if (scopeItems && scopeItems.length > 0) {
    metCount = scopeItems.reduce((sum, item) => sum + item.totalAssigned, 0);
    requiredCount = scopeItems.reduce((sum, item) => sum + item.totalAssigned + item.totalUnassigned, 0);
  } else {
    metCount = typeof raw.metCount === 'number' ? raw.metCount : undefined;
    requiredCount = typeof raw.requiredCount === 'number' ? raw.requiredCount : undefined;
  }

  const certificateIdFromDto = dto.permitCertificateId != null ? Number(dto.permitCertificateId) : undefined;
  const certificateIdFromRaw = typeof raw.certificateId === 'number' ? raw.certificateId : undefined;

  return {
    id: Number(dto.id ?? 0),
    requirementType,
    level,
    name: dto.permitCertificateName ?? '',
    description: (dto.requirement as string) ?? null,
    assignedTo: dto.assignedTo ?? null,
    isFulfilled: dto.isFulfilled === true,
    certificateId: certificateIdFromDto ?? certificateIdFromRaw,
    assignmentType:
      dto.scope?.scopeType === 1
        ? 'equipment'
        : raw.assignmentType === 'employee' || raw.assignmentType === 'equipment'
          ? (raw.assignmentType as 'employee' | 'equipment')
          : 'employee',
    targetId: typeof raw.targetId === 'number' ? raw.targetId : undefined,
    targetName: typeof raw.targetName === 'string' ? raw.targetName : undefined,
    requiredCount,
    metCount,
    fileName: typeof raw.fileName === 'string' ? raw.fileName : undefined,
    fileSize: typeof raw.fileSize === 'string' ? raw.fileSize : undefined,
    scopeType,
    scopeItems
  };
}

export function mapRequirementDtoList(dtos: PermitCertificateRequirementRequiredItemDto[]): JobRequirement[] {
  return dtos.map(mapRequirementDto);
}

export function mapJobSiteRequiredPermitDto(dto: AssignedPermitDto): JobRequirement {
  const name = dto.permitName ?? dto.permit?.name ?? '';
  const assignedTo = dto.resource?.name ?? null;

  return {
    id: Number(dto.id ?? 0),
    requirementType: 'permit',
    level: 'jobSite',
    name,
    description: null,
    assignedTo,
    isFulfilled: Boolean(assignedTo)
  };
}

export function mapJobSiteRequiredPermitDtoList(dtos: AssignedPermitDto[]): JobRequirement[] {
  return dtos.map(mapJobSiteRequiredPermitDto);
}
